import React, { useState } from "react";
import { useParams } from "react-router";
import { resetPassword } from "../../utils/Auth";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { Grid } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Typography } from "@mui/material";
import { FormControl, InputLabel, OutlinedInput } from "@mui/material";
import { FormHelperText } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../actions/snackbarActions";
import { logOut } from "../../actions/authActions";
import PasswordChanged from "../../image/PasswordChanged.svg";
import Tooltip from "@mui/material/Tooltip";
import {
  getPasswordValidationErrors,
  isPasswordFormatValid,
} from "../../utils/passwordValidation";

/**
 * After clicking a link in the mail for password recovery this page is prompt.
 * Here user can enter new password and by doing updates his old password that he forgot.
 */
function ResetPassword() {
  const { id, token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pwdError, setPwdError] = useState(false);
  const [confirmPwdError, setConfirmPwdError] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [values, setValues] = useState({
    showPassword: false,
    showPassword1: false,
    showPassword2: false,
  });

  const validatePasswordValue = (value) => {
    if (!value) {
      setPwdError(false);
      setPasswordErrors([]);
      return [];
    }
    const errors = getPasswordValidationErrors(value, t);
    setPwdError(errors.length > 0);
    setPasswordErrors(errors);
    return errors;
  };

  const validateConfirmPasswordValue = (pwd, confirmPassword) => {
    if (!confirmPassword) {
      setConfirmPwdError(false);
      setConfirmPasswordErrors([]);
      return [];
    }

    const errors = [...getPasswordValidationErrors(confirmPassword, t)];
    if (pwd !== confirmPassword) {
      errors.push(t("errorHelperText.passwordMatch"));
    }

    const unique = [...new Set(errors)];
    setConfirmPwdError(unique.length > 0);
    setConfirmPasswordErrors(unique);
    return unique;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePasswordValue(value);
    if (confirmPw) {
      validateConfirmPasswordValue(value, confirmPw);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPw(value);
    validateConfirmPasswordValue(password, value);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    if (!oldPassword.trim()) {
      dispatch(setSnackbar(true, "error", t("snackbar.mandatoryFields")));
      return;
    }

    const pwd = password.trim();
    const confirm = confirmPw.trim();

    if (!pwd || !confirm) {
      const mandatory = t("snackbar.mandatoryFields");
      if (!pwd) {
        setPwdError(true);
        setPasswordErrors([mandatory]);
      }
      if (!confirm) {
        setConfirmPwdError(true);
        setConfirmPasswordErrors([mandatory]);
      }
      dispatch(setSnackbar(true, "error", mandatory));
      return;
    }

    if (password !== confirmPw) {
      const matchError = t("errorHelperText.passwordMatch");
      setConfirmPwdError(true);
      setConfirmPasswordErrors([matchError]);
      dispatch(setSnackbar(true, "error", matchError));
      return;
    }

    const pwdErrors = validatePasswordValue(password);
    const confirmErrors = validateConfirmPasswordValue(password, confirmPw);

    if (
      pwdErrors.length ||
      confirmErrors.length ||
      !isPasswordFormatValid(password)
    ) {
      dispatch(
        setSnackbar(
          true,
          "error",
          pwdErrors[0] ||
            confirmErrors[0] ||
            t("snackbar.passwordChangedError"),
        ),
      );
      return;
    }

    setIsSubmitting(true);

    resetPassword(id, token, password, oldPassword)
      .then((response) => {
        const status = Number(response?.statusCode ?? response?.status);
        if (status >= 200 && status < 300) {
          // Force logout and return to login
          localStorage.removeItem("userName");
          dispatch(logOut());
          navigate("/", {
            replace: true,
            state: { passwordResetSuccess: true },
          });
          return;
        }

        setIsSubmitting(false);
        dispatch(
          setSnackbar(
            true,
            "error",
            response?.message || t("snackbar.passwordChangedError"),
          ),
        );
      })
      .catch((err) => {
        setIsSubmitting(false);
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.message ||
              err?.message ||
              t("snackbar.passwordChangedError"),
          ),
        );
      });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleClickShowPassword1 = () => {
    setValues({ ...values, showPassword1: !values.showPassword1 });
  };

  const handleClickShowPassword2 = () => {
    setValues({ ...values, showPassword2: !values.showPassword2 });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const eyeAdornment = (show, onClick) => (
    <InputAdornment position="end" sx={{ right: 20, position: "absolute" }}>
      <IconButton
        aria-label="toggle password visibility"
        onClick={onClick}
        onMouseDown={handleMouseDownPassword}
        edge="end"
        tabIndex={-1}
      >
        {show ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        className="con"
        component="main"
        maxWidth="md"
        sx={{
          mb: 4,
          height: "calc(72vh - 50px)",
          width: { xs: 200, sm: 400, md: 400, lg: 500, xl: 600 },
        }}
      >
        <Paper
          variant="outlined"
          sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
        >
          {!resetDone ? (
            <Box
              component="form"
              noValidate
              sx={{ mt: 1 }}
              onSubmit={onSubmit}
            >
              <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: "10vh" }}
              >
                <Grid item xs={3}>
                  <LockResetIcon
                    m="auto"
                    sx={{ fontSize: 75, color: theme.palette.secondary.main }}
                  />
                </Grid>
              </Grid>

              <FormControl fullWidth required sx={{ mt: 2 }}>
                <InputLabel htmlFor="reset-old-password">
                  {t("resetpassword.pasOld")}
                </InputLabel>
                <OutlinedInput
                  id="reset-old-password"
                  name="oldPassword"
                  label={t("resetpassword.pasOld")}
                  type={values.showPassword2 ? "text" : "password"}
                  autoComplete="current-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  endAdornment={eyeAdornment(
                    values.showPassword2,
                    handleClickShowPassword2,
                  )}
                />
              </FormControl>

              <Tooltip
                title={t("registration.passwordTooltip")}
                placement="top-start"
              >
                <FormControl fullWidth required sx={{ mt: 2 }} error={pwdError}>
                  <InputLabel htmlFor="reset-new-password">
                    {t("resetpassword.pas")}
                  </InputLabel>
                  <OutlinedInput
                    id="reset-new-password"
                    name="password"
                    label={t("resetpassword.pas")}
                    type={values.showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={(e) => validatePasswordValue(e.target.value)}
                    error={pwdError}
                    endAdornment={eyeAdornment(
                      values.showPassword,
                      handleClickShowPassword,
                    )}
                  />
                  {passwordErrors.map((errorText, index) => (
                    <FormHelperText error key={`pwd-err-${index}`}>
                      {errorText}
                    </FormHelperText>
                  ))}
                </FormControl>
              </Tooltip>

              <FormControl
                fullWidth
                required
                sx={{ mt: 2 }}
                error={confirmPwdError}
              >
                <InputLabel htmlFor="reset-confirm-password">
                  {t("resetpassword.confirmpas")}
                </InputLabel>
                <OutlinedInput
                  id="reset-confirm-password"
                  name="confirmPassword"
                  label={t("resetpassword.confirmpas")}
                  type={values.showPassword1 ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={handleConfirmPasswordChange}
                  onBlur={() =>
                    validateConfirmPasswordValue(password, confirmPw)
                  }
                  error={confirmPwdError}
                  endAdornment={eyeAdornment(
                    values.showPassword1,
                    handleClickShowPassword1,
                  )}
                />
                {confirmPasswordErrors.map((errorText, index) => (
                  <FormHelperText error key={`confirm-pwd-err-${index}`}>
                    {errorText}
                  </FormHelperText>
                ))}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: theme.palette.secondary.main,
                  fontSize: 20,
                  "&.Mui-disabled": {
                    background: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {t("resetpassword.pasbtn")}
              </Button>
            </Box>
          ) : (
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
              py={2}
              sx={{ minHeight: "50vh" }}
            >
              <Grid item xs={12}>
                <Typography variant="h4">
                  Success! Go back to home page.
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Box>
                  <img src={PasswordChanged} alt="Password changed" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={() => navigate("/")}
                  size="large"
                  variant="contained"
                  sx={{ backgroundColor: theme.palette.secondary.main }}
                >
                  {t("resetpassword.click")} {t("resetpassword.here")}
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
}

export default ResetPassword;
